import React from 'react';
import { AesLine } from 'api/AesAnalyzerAPI';
import { routedFlag, usmlDisplay } from 'components/Analyzers/aesFormat';
import { PickerColumn } from './tableColumns';

export const AES_TABLE_KEY = 'aes-run-lines';

export type AesColumnKey =
  | 'shipment'
  | 'itn'
  | 'line'
  | 'export_date'
  | 'filing_date'
  | 'filing_option'
  | 'usppi'
  | 'consignee'
  | 'consignee_country'
  | 'intermediate_consignee'
  | 'authorized_agent'
  | 'routed'
  | 'destination'
  | 'license'
  | 'license_value'
  | 'eccn'
  | 'schedule_b'
  | 'description'
  | 'quantity'
  | 'value'
  | 'export_info_code'
  | 'pga';

export interface AesRenderContext {
  hasRoutedColumn: boolean;
}

export interface AesColumn extends PickerColumn<AesColumnKey> {
  header: string;
  sortKey?: string;
  headerStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  /**
   * Upload fields the column reads. When none of them was mapped from this
   * run's file the column has nothing to show, so it is offered disabled.
   */
  fields?: string[];
  render: (line: AesLine, ctx: AesRenderContext) => React.ReactNode;
}

const money = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '--';
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n)
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(n)
    : String(v);
};

const field = (line: AesLine, key: string): string =>
  line.fields?.[key] || '--';

const nameWithCountry = (name?: string, country?: string): React.ReactNode =>
  name || country ? (
    <>
      {name || '--'}
      {country && (
        <span style={{ color: 'var(--text-secondary)' }}> ({country})</span>
      )}
    </>
  ) : (
    '--'
  );

const ellipsis: React.CSSProperties = {
  maxWidth: 200,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const numeric: React.CSSProperties = {
  fontFamily: 'Inter',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

export const AES_COLUMNS: readonly AesColumn[] = [
  {
    key: 'shipment',
    label: 'Shipment',
    defaultVisible: true,
    header: 'Shipment',
    sortKey: 'shipment_reference',
    render: (l) => l.shipment_reference || l.itn || '--',
  },
  {
    key: 'itn',
    label: 'ITN',
    header: 'ITN',
    sortKey: 'itn',
    fields: ['itn'],
    cellStyle: { whiteSpace: 'nowrap' },
    render: (l) => l.itn || '--',
  },
  {
    key: 'line',
    label: 'Line',
    defaultVisible: true,
    header: 'Line',
    cellStyle: { fontVariantNumeric: 'tabular-nums' },
    render: (l) => l.line_number || '--',
  },
  {
    key: 'export_date',
    label: 'Export Date',
    header: 'Export Date',
    fields: ['export_date'],
    cellStyle: { whiteSpace: 'nowrap' },
    render: (l) => l.export_date || '--',
  },
  {
    key: 'filing_date',
    label: 'EEI Filing Date',
    header: 'Filing Date',
    fields: ['eei_filing_date'],
    cellStyle: { whiteSpace: 'nowrap' },
    render: (l) => field(l, 'eei_filing_date'),
  },
  {
    key: 'filing_option',
    label: 'Filing Option',
    header: 'Filing Option',
    sortKey: 'filing_option',
    fields: ['filing_option'],
    render: (l) => field(l, 'filing_option'),
  },
  {
    key: 'usppi',
    label: 'USPPI',
    header: 'USPPI',
    sortKey: 'usppi_name',
    fields: ['usppi_name'],
    cellStyle: ellipsis,
    render: (l) => (
      <span title={l.fields?.usppi_name || undefined}>
        {field(l, 'usppi_name')}
      </span>
    ),
  },
  {
    key: 'consignee',
    label: 'Consignee',
    defaultVisible: true,
    header: 'Consignee',
    sortKey: 'consignee_name',
    cellStyle: ellipsis,
    render: (l) => (
      <span title={l.consignee_name || undefined}>
        {l.consignee_name || '--'}
      </span>
    ),
  },
  {
    key: 'consignee_country',
    label: 'Consignee Country',
    header: 'Consignee Ctry.',
    sortKey: 'consignee_country',
    fields: ['consignee_country'],
    render: (l) => field(l, 'consignee_country'),
  },
  {
    key: 'intermediate_consignee',
    label: 'Intermediate Consignee',
    header: 'Intermediate Consignee',
    sortKey: 'intermediate_consignee_name',
    fields: ['intermediate_consignee_name', 'intermediate_consignee_country'],
    cellStyle: { whiteSpace: 'nowrap' },
    render: (l) =>
      nameWithCountry(
        l.fields?.intermediate_consignee_name,
        l.fields?.intermediate_consignee_country,
      ),
  },
  {
    key: 'authorized_agent',
    label: 'Authorized Agent',
    header: 'Authorized Agent',
    sortKey: 'authorized_agent_name',
    fields: ['authorized_agent_name'],
    cellStyle: ellipsis,
    render: (l) => field(l, 'authorized_agent_name'),
  },
  {
    key: 'routed',
    label: 'Routed',
    defaultVisible: true,
    header: 'Routed',
    render: (l: AesLine, ctx: AesRenderContext): React.ReactNode => {
      const routed = routedFlag(l.fields, ctx.hasRoutedColumn);
      return routed ? (
        <span
          className={`aes-routed-tag ${routed === 'Y' ? 'yes' : ''}`}
          title={
            routed === 'Y'
              ? 'Routed export transaction'
              : 'Not a routed transaction'
          }
        >
          {routed}
        </span>
      ) : (
        '--'
      );
    },
  },
  {
    key: 'destination',
    label: 'Destination',
    defaultVisible: true,
    header: 'Dest.',
    sortKey: 'destination_country',
    render: (l) => l.destination_country || '--',
  },
  {
    key: 'license',
    label: 'License',
    defaultVisible: true,
    header: 'License',
    sortKey: 'license_type',
    cellStyle: { whiteSpace: 'nowrap' },
    render: (l) => (
      <>
        {l.license_type || '--'}
        {l.license_number && (
          <span style={{ color: 'var(--text-secondary)' }}>
            {' '}
            / {l.license_number}
          </span>
        )}
      </>
    ),
  },
  {
    key: 'license_value',
    label: 'License Value',
    header: 'License Value',
    sortKey: 'license_value',
    fields: ['license_value'],
    cellStyle: numeric,
    render: (l) => money(l.fields?.license_value),
  },
  {
    key: 'eccn',
    label: 'ECCN / USML',
    defaultVisible: true,
    header: 'ECCN / USML',
    sortKey: 'eccn',
    cellStyle: { fontFamily: 'Inter' },
    render: (l) =>
      [l.eccn, l.usml_category ? `USML ${usmlDisplay(l.usml_category)}` : null]
        .filter(Boolean)
        .join(' / ') || '--',
  },
  {
    key: 'schedule_b',
    label: 'Schedule B',
    defaultVisible: true,
    header: 'Schedule B',
    sortKey: 'hts',
    cellStyle: { fontFamily: 'Inter' },
    render: (l) => l.hts || '--',
  },
  {
    key: 'description',
    label: 'Description',
    header: 'Description',
    fields: ['description'],
    cellStyle: ellipsis,
    render: (l) => (
      <span title={l.description || undefined}>{l.description || '--'}</span>
    ),
  },
  {
    key: 'quantity',
    label: 'Quantity',
    header: 'Qty',
    sortKey: 'quantity1',
    fields: ['quantity1'],
    cellStyle: numeric,
    render: (l) => field(l, 'quantity1'),
  },
  {
    key: 'value',
    label: 'Value',
    defaultVisible: true,
    header: 'Value',
    sortKey: 'line_value',
    cellStyle: numeric,
    render: (l) => money(l.line_value),
  },
  {
    key: 'export_info_code',
    label: 'Export Info Code',
    header: 'EIC',
    sortKey: 'export_info_code',
    fields: ['export_info_code'],
    render: (l) => field(l, 'export_info_code'),
  },
  {
    key: 'pga',
    label: 'PGA Indicator',
    header: 'PGA',
    sortKey: 'pga_indicator',
    fields: ['pga_indicator'],
    render: (l) => field(l, 'pga_indicator'),
  },
];

/** Optional columns this run's upload has no data for, with the reason to show. */
export const unavailableAesColumns = (
  columnMap: Record<string, string> | undefined,
): Partial<Record<AesColumnKey, string>> => {
  if (!columnMap) return {};
  const out: Partial<Record<AesColumnKey, string>> = {};
  AES_COLUMNS.forEach((c) => {
    if (c.fields && !c.fields.some((f) => columnMap[f]))
      out[c.key] = 'Not in this upload';
  });
  return out;
};
